import { api } from '../api';
import type {
  ApprovalChainRow,
  ComplianceDashboard,
  ComplianceOperationsDetail,
  ComplianceOperationsItem,
  ComplianceOperationsSummary,
  EntityComplianceRow,
  OperationsRadarFilter,
  ProfileAssignmentRow,
  RequirementProfileItemRow,
  RequirementProfileRow,
} from '@/types/domain';

export const requirementProfilesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listRequirementProfiles: builder.query<RequirementProfileRow[], { contextId?: number; active?: boolean } | void>({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.contextId) search.set('contextId', String(params.contextId));
        if (params?.active !== undefined) search.set('active', String(params.active));
        const qs = search.toString();
        return qs ? `/requirement-profiles?${qs}` : '/requirement-profiles';
      },
      providesTags: [{ type: 'RequirementProfile', id: 'LIST' }],
    }),
    getRequirementProfile: builder.query<RequirementProfileRow, number>({
      query: (id) => `/requirement-profiles/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'RequirementProfile', id }],
    }),
    getRequirementProfileAssignments: builder.query<ProfileAssignmentRow, number>({
      query: (id) => `/requirement-profiles/${id}/assignments`,
      providesTags: (_r, _e, id) => [{ type: 'RequirementProfile', id: `assignments-${id}` }],
    }),
    createRequirementProfile: builder.mutation<RequirementProfileRow, Record<string, unknown>>({
      query: (body) => ({ url: '/requirement-profiles', method: 'POST', body }),
      invalidatesTags: [{ type: 'RequirementProfile', id: 'LIST' }],
    }),
    updateRequirementProfile: builder.mutation<RequirementProfileRow, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/requirement-profiles/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'RequirementProfile', id }, { type: 'RequirementProfile', id: 'LIST' }],
    }),
    deleteRequirementProfile: builder.mutation<void, number>({
      query: (id) => ({ url: `/requirement-profiles/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'RequirementProfile', id: 'LIST' }],
    }),
    duplicateRequirementProfile: builder.mutation<RequirementProfileRow, { id: number; name?: string }>({
      query: ({ id, name }) => ({
        url: `/requirement-profiles/${id}/duplicate`,
        method: 'POST',
        body: name ? { name } : {},
      }),
      invalidatesTags: [{ type: 'RequirementProfile', id: 'LIST' }],
    }),
    addProfileItem: builder.mutation<RequirementProfileItemRow, { profileId: number; body: Record<string, unknown> }>({
      query: ({ profileId, body }) => ({
        url: `/requirement-profiles/${profileId}/items`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { profileId }) => [{ type: 'RequirementProfile', id: profileId }],
    }),
    updateProfileItem: builder.mutation<
      RequirementProfileItemRow,
      { itemId: number; profileId: number; body: Record<string, unknown> }
    >({
      query: ({ itemId, body }) => ({
        url: `/requirement-profile-items/${itemId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { profileId }) => [{ type: 'RequirementProfile', id: profileId }],
    }),
    deleteProfileItem: builder.mutation<void, { itemId: number; profileId: number }>({
      query: ({ itemId }) => ({
        url: `/requirement-profile-items/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { profileId }) => [{ type: 'RequirementProfile', id: profileId }],
    }),
  }),
});

export const complianceApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getComplianceDashboard: builder.query<ComplianceDashboard, { contextId?: number; limit?: number } | void>({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.contextId) search.set('contextId', String(params.contextId));
        if (params?.limit) search.set('limit', String(params.limit));
        const qs = search.toString();
        return qs ? `/compliance/dashboard?${qs}` : '/compliance/dashboard';
      },
      providesTags: [{ type: 'Compliance', id: 'DASHBOARD' }],
    }),
    getEntityCompliance: builder.query<EntityComplianceRow[], { entityId: number; contextId?: number }>({
      query: ({ entityId, contextId }) => {
        const search = new URLSearchParams();
        if (contextId) search.set('contextId', String(contextId));
        const qs = search.toString();
        return qs
          ? `/entities/${entityId}/compliance?${qs}`
          : `/entities/${entityId}/compliance`;
      },
    }),
    getComplianceOperationsSummary: builder.query<
      ComplianceOperationsSummary,
      { contextId?: number } | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.contextId) search.set('contextId', String(params.contextId));
        const qs = search.toString();
        return qs ? `/compliance/operations/summary?${qs}` : '/compliance/operations/summary';
      },
      providesTags: [{ type: 'Compliance', id: 'OPERATIONS_SUMMARY' }],
    }),
    getComplianceOperationsItems: builder.query<
      { items: ComplianceOperationsItem[]; total: number; limit: number; offset: number },
      {
        contextId?: number;
        q?: string;
        filter?: OperationsRadarFilter;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.contextId) search.set('contextId', String(params.contextId));
        if (params?.q) search.set('q', params.q.slice(0, 128));
        if (params?.filter) search.set('filter', params.filter);
        if (params?.limit) search.set('limit', String(params.limit));
        if (params?.offset) search.set('offset', String(params.offset));
        const qs = search.toString();
        return qs ? `/compliance/operations/items?${qs}` : '/compliance/operations/items';
      },
      providesTags: [{ type: 'Compliance', id: 'OPERATIONS_ITEMS' }],
    }),
    getComplianceOperationsDetail: builder.query<ComplianceOperationsDetail, number>({
      query: (id) => `/compliance/operations/items/${id}/detail`,
      providesTags: (_r, _e, id) => [{ type: 'Compliance', id: `OPERATIONS_DETAIL_${id}` }],
    }),
    getAssociationDocumentStatus: builder.query<
      ComplianceOperationsDetail & {
        summary: { vigentes: number; porVencer: number; vencidos: number; pendientes: number; faltantes: number };
        permissions: {
          canUploadDoc: boolean;
          canExport: boolean;
          canEditAssociation: boolean;
        };
        requirenteContextId: number | null;
      },
      { id: number; state?: string; q?: string; requirenteContextId?: number }
    >({
      query: ({ id, state, q, requirenteContextId }) => {
        const search = new URLSearchParams();
        if (state) search.set('state', state.slice(0, 32));
        if (q) search.set('q', q.slice(0, 128));
        if (requirenteContextId) search.set('requirenteContextId', String(requirenteContextId));
        const qs = search.toString();
        return qs
          ? `/associations/${id}/document-status?${qs}`
          : `/associations/${id}/document-status`;
      },
      providesTags: (_r, _e, { id }) => [{ type: 'Compliance', id: `DOC_STATUS_${id}` }],
    }),
  }),
});

export const approvalChainsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listApprovalChains: builder.query<ApprovalChainRow[], void>({
      query: () => '/approval-chains',
      providesTags: [{ type: 'ApprovalChain', id: 'LIST' }],
    }),
    getApprovalChain: builder.query<ApprovalChainRow, number>({
      query: (id) => `/approval-chains/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'ApprovalChain', id }],
    }),
    createApprovalChain: builder.mutation<ApprovalChainRow, Record<string, unknown>>({
      query: (body) => ({ url: '/approval-chains', method: 'POST', body }),
      invalidatesTags: [{ type: 'ApprovalChain', id: 'LIST' }],
    }),
    updateApprovalChain: builder.mutation<ApprovalChainRow, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/approval-chains/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'ApprovalChain', id }, { type: 'ApprovalChain', id: 'LIST' }],
    }),
    deleteApprovalChain: builder.mutation<void, number>({
      query: (id) => ({ url: `/approval-chains/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'ApprovalChain', id: 'LIST' }],
    }),
  }),
});

export const approvalApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getApprovalQueue: builder.query<{ items: unknown[] }, void>({
      query: () => '/approval/queue',
      providesTags: [{ type: 'Approval', id: 'QUEUE' }],
    }),
  }),
});

export const {
  useListRequirementProfilesQuery,
  useGetRequirementProfileQuery,
  useGetRequirementProfileAssignmentsQuery,
  useCreateRequirementProfileMutation,
  useUpdateRequirementProfileMutation,
  useDeleteRequirementProfileMutation,
  useDuplicateRequirementProfileMutation,
  useAddProfileItemMutation,
  useUpdateProfileItemMutation,
  useDeleteProfileItemMutation,
} = requirementProfilesApi;

export const {
  useGetComplianceDashboardQuery,
  useGetEntityComplianceQuery,
  useGetComplianceOperationsSummaryQuery,
  useGetComplianceOperationsItemsQuery,
  useGetComplianceOperationsDetailQuery,
  useGetAssociationDocumentStatusQuery,
} = complianceApi;

export const {
  useListApprovalChainsQuery,
  useGetApprovalChainQuery,
  useCreateApprovalChainMutation,
  useUpdateApprovalChainMutation,
  useDeleteApprovalChainMutation,
} = approvalChainsApi;

export const { useGetApprovalQueueQuery } = approvalApi;
